import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ScnLogo from "./assets/img/scnlogo.png";
import { Context } from "../../context/Context";

/* ─── Nav links data ──────────────────────────────────────────── */
const buildNavLinks = (user) => [
  { label: "Home", to: "/home" },
  { label: "How To", to: "/how-to" },
  ...(!user ? [{ label: "Apply Now", to: "/application" }] : []),
  { label: "Contact", to: "/contact" },
];

/* ─── Scroll-triggered elevation helper ──────────────────────── */
function ElevationScroll({ children }) {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 10 });
  return React.cloneElement(children, {
    className: `scn-appbar${trigger ? " scrolled" : ""}`,
    elevation: trigger ? 4 : 0,
  });
}

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useContext(Context);
  const location = useLocation();
  const navLinks = buildNavLinks(user);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  const isActive = (to) => location.pathname === to;

  /* ─── Mobile Drawer ──────────────────────────────────────────── */
  const mobileDrawer = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          background: "linear-gradient(160deg, #004D2C 0%, #006B3F 100%)",
          color: "#fff",
        },
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <img src={ScnLogo} alt="SCN Logo" style={{ width: 40, height: 40 }} />
          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}
            >
              Supreme Court
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
              of Nigeria
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={() => setDrawerOpen(false)}
          sx={{ color: "rgba(255,255,255,0.7)" }}
          aria-label="Close navigation menu"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Drawer Nav Links */}
      <List sx={{ pt: 2, px: 1 }}>
        {navLinks.map((link) => (
          <ListItem key={link.to} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={link.to}
              selected={isActive(link.to)}
              sx={{
                borderRadius: "10px",
                color: isActive(link.to)
                  ? "#ffffff"
                  : "rgba(255,255,255,0.75)",
                background: isActive(link.to)
                  ? "rgba(201,168,76,0.18)"
                  : "transparent",
                borderLeft: isActive(link.to)
                  ? "3px solid #C9A84C"
                  : "3px solid transparent",
                fontWeight: isActive(link.to) ? 700 : 500,
                "&:hover": {
                  background: "rgba(255,255,255,0.08)",
                  color: "#ffffff",
                },
                "& .MuiListItemText-primary": {
                  fontSize: 14,
                  fontWeight: "inherit",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontFamily: "'Inter', sans-serif",
                },
              }}
            >
              <ListItemText primary={link.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />

      {/* Login CTA in drawer */}
      <Box sx={{ px: 2 }}>
        <Button
          component={Link}
          to="/login"
          fullWidth
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            background: "linear-gradient(135deg, #C9A84C 0%, #A8872E 100%)",
            color: "#004D2C",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            borderRadius: "10px",
            py: 1.4,
            fontFamily: "'Inter', sans-serif",
            "&:hover": {
              background: "linear-gradient(135deg, #D4B45C 0%, #C9A84C 100%)",
            },
          }}
        >
          Login
        </Button>
      </Box>

      {/* Footer note */}
      <Box sx={{ mt: "auto", p: 2, textAlign: "center" }}>
        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
          Federal Judiciary — Ubi jus ibi remedium
        </Typography>
      </Box>
    </Drawer>
  );

  return (
    <>
      {/* ── Top Info Bar ─────────────────────────────────────────── */}
      <Box className="scn-top-bar">
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, md: 4 },
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", md: "flex-start" },
            gap: 1,
          }}
        >
          <EmailOutlinedIcon sx={{ fontSize: 14, opacity: 0.75 }} />
          <a href="mailto:notary@supremecourt.gov.ng">
            notary@supremecourt.gov.ng
          </a>
          <Box
            component="span"
            sx={{
              mx: 1.5,
              color: "rgba(255,255,255,0.25)",
              display: { xs: "none", md: "inline" },
            }}
          >
            |
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 12,
              color: "rgba(255,255,255,0.6)",
              display: { xs: "none", md: "inline" },
            }}
          >
            Official Notary Public Application Portal
          </Box>
        </Box>
      </Box>

      {/* ── Main Navigation ──────────────────────────────────────── */}
      <ElevationScroll>
        <AppBar position="sticky" className="scn-appbar">
          <Toolbar
            sx={{
              maxWidth: 1200,
              width: "100%",
              mx: "auto",
              px: { xs: 2, md: 4 },
              py: 0,
              minHeight: { xs: 64, md: 72 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Logo */}
            <Box
              component={Link}
              to={process.env.PUBLIC_URL || "/home"}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                textDecoration: "none",
                "& img": {
                  transition: "transform 0.3s ease",
                },
                "&:hover img": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <img
                src={ScnLogo}
                alt="Supreme Court of Nigeria Logo"
                style={{ width: 60, height: 60 }}
              />
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#004D2C",
                    lineHeight: 1.15,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Supreme Court of Nigeria
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: "#4A5E52",
                    letterSpacing: "0.04em",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Notary Public Portal
                </Typography>
              </Box>
            </Box>

            {/* Desktop nav links */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 4,
              }}
            >
              {navLinks.map((link) => (
                <Box
                  key={link.to}
                  component={Link}
                  to={link.to}
                  className={`scn-nav-link${isActive(link.to) ? " active" : ""}`}
                  sx={{ textDecoration: "none" }}
                >
                  {link.label}
                </Box>
              ))}
            </Box>

            {/* Login button + mobile toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  display: { xs: "none", md: "flex" },
                  background: "linear-gradient(135deg, #006B3F 0%, #004D2C 100%)",
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  borderRadius: "8px",
                  px: 2.5,
                  py: 1.1,
                  fontFamily: "'Inter', sans-serif",
                  border: "1px solid rgba(201,168,76,0.4)",
                  boxShadow: "0 2px 12px rgba(0,77,44,0.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #008751 0%, #006B3F 100%)",
                    boxShadow: "0 4px 20px rgba(0,77,44,0.35)",
                  },
                }}
              >
                Login
              </Button>

              {/* Mobile hamburger */}
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  display: { xs: "flex", md: "none" },
                  color: "#004D2C",
                  border: "1px solid rgba(0,107,63,0.2)",
                  borderRadius: "8px",
                  p: "7px",
                }}
                aria-label="Open navigation menu"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      </ElevationScroll>

      {/* Mobile drawer */}
      {mobileDrawer}
    </>
  );
};

export default Header;
